import React, { useEffect, useMemo, useState } from 'react';
import DatePickerInput from '../../../../Components/Form/Inputs/DatePickerInput';
import RadioInput from '../../../../Components/Form/Inputs/RadioInput';
import ActionModal from '../../../../Components/Modals/ActionModal/ActionModal';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from './MarketAccess.module.scss';
import {
    addDaysToStartOfToday,
    calculateMarketAccessExpirationDate,
    MarketAccessExpiration,
    MarketAccessExpirationOption,
    maximumRecommendedGrantNumberOfDays,
    startOfToday,
} from './MarketAccessDefinitions';

interface IMarketAccessExpirationModalProps {
    showModal: boolean;
    expiration: MarketAccessExpiration;
    accountExpirationDate?: Date;
    handleSave: (expiration: MarketAccessExpiration) => void;
    handleApplyToAllMarkets: (expiration: MarketAccessExpiration) => void;
    handleCancel: () => void;
}

const expirationOptionOrder: MarketAccessExpirationOption[] = ['fourteenDays', 'thirtyDays', 'custom', 'permanent'];

const MarketAccessExpirationModal: React.FC<IMarketAccessExpirationModalProps> = (props: IMarketAccessExpirationModalProps) => {
    const [translations] = useLanguage();
    const [expirationOption, setExpirationOption] = useState<MarketAccessExpirationOption>(props.expiration.expirationOption);
    const [customExpirationDate, setCustomExpirationDate] = useState<Date | undefined>(props.expiration.expirationDate);
    const [isCustomExpirationDateFieldValid, setIsCustomExpirationDateFieldValid] = useState(true);

    const { market } = props.expiration;

    // Every time the modal is opened for a market it starts from the period currently stored for that market.
    useEffect(() => {
        if (!props.showModal) {
            return;
        }

        setExpirationOption(props.expiration.expirationOption);
        setCustomExpirationDate(props.expiration.expirationOption === 'custom' ? props.expiration.expirationDate : undefined);
        setIsCustomExpirationDateFieldValid(true);
    }, [props.showModal, market]);

    const expirationOptionLabels = useMemo<{ [key in MarketAccessExpirationOption]: string }>(
        () => ({
            fourteenDays: translations.users.trialLength.fourteenDays,
            thirtyDays: translations.users.trialLength.thirtyDays,
            custom: translations.users.trialLength.custom,
            permanent: translations.users.marketAccess.permanent,
        }),
        [translations],
    );

    const pendingExpirationDate = useMemo(
        () => calculateMarketAccessExpirationDate(expirationOption, customExpirationDate),
        [expirationOption, customExpirationDate],
    );

    const errorMessage = useMemo(() => {
        if (expirationOption === 'custom') {
            if (!customExpirationDate || Number.isNaN(customExpirationDate.getTime())) {
                return translations.users.errors.marketAccessEndDateRequired;
            }

            if (customExpirationDate.getTime() <= startOfToday().getTime()) {
                return translations.users.errors.marketAccessEndDateNotInFuture;
            }
        }

        // The api rejects any sector end date that outlives the account, so it is caught before saving.
        if (pendingExpirationDate && props.accountExpirationDate && pendingExpirationDate.getTime() > props.accountExpirationDate.getTime()) {
            return `${translations.users.errors.marketAccessEndDateAfterAccountExpiration} ${formattingService.toLongDayMonthYear(
                props.accountExpirationDate,
            )}.`;
        }

        return undefined;
    }, [expirationOption, customExpirationDate, pendingExpirationDate, props.accountExpirationDate, translations]);

    // The date field renders its own message for a malformed date, so an invalid field only has to block saving here.
    const hasError = !!errorMessage || (expirationOption === 'custom' && !isCustomExpirationDateFieldValid);

    const warningMessage = useMemo(() => {
        if (hasError || !pendingExpirationDate) {
            return undefined;
        }

        const longestRecommendedDate = addDaysToStartOfToday(maximumRecommendedGrantNumberOfDays);

        return pendingExpirationDate.getTime() > longestRecommendedDate.getTime() ? translations.users.errors.marketAccessGrantLongerThanOneYear : undefined;
    }, [hasError, pendingExpirationDate, translations]);

    const buildExpiration = (): MarketAccessExpiration => ({
        market,
        expirationOption,
        expirationDate: pendingExpirationDate,
    });

    return (
        <ActionModal
            header={`${translations.market[market]} — ${translations.users.marketAccess.accessPeriod}`}
            showModal={props.showModal}
            actionButtonName={translations.actions.save}
            handleAction={() => props.handleSave(buildExpiration())}
            actionButtonDisabled={hasError}
            secondaryActionButtonName={translations.users.marketAccess.applyToAllSectors}
            handleSecondaryAction={() => props.handleApplyToAllMarkets(buildExpiration())}
            secondaryActionButtonDisabled={hasError}
            handleCancel={props.handleCancel}
            track={{ eventCategory: 'User', eventAction: 'MarketAccessExpirationModal', eventValue: market }}
        >
            <div className={styles.market_access_modal_content}>
                <fieldset className={styles.market_access_radio_group}>
                    <legend className={styles.market_access_radio_group_legend}>{translations.users.marketAccess.accessPeriod}</legend>
                    {expirationOptionOrder.map((option) => (
                        <RadioInput
                            key={option}
                            title={expirationOptionLabels[option]}
                            isChecked={expirationOption === option}
                            handleIsChecked={(isChecked) => {
                                if (!isChecked) {
                                    return;
                                }

                                setExpirationOption(option);
                            }}
                            track={{ eventCategory: 'User', eventAction: 'MarketAccessExpirationOptionChanged', eventValue: `${market}-${option}` }}
                        />
                    ))}
                </fieldset>
                {expirationOption === 'custom' ? (
                    <DatePickerInput
                        name="MarketAccessExpirationDate"
                        required
                        value={customExpirationDate ?? null}
                        title={translations.users.fields.expiration}
                        oldestAllowedDate={addDaysToStartOfToday(1)}
                        mostRecentAllowedDate={props.accountExpirationDate}
                        onValidityChange={setIsCustomExpirationDateFieldValid}
                        handleDateChange={(value) => setCustomExpirationDate(value)}
                        track={{ eventCategory: 'User', eventAction: 'MarketAccessExpirationDateChanged', eventValue: market }}
                    />
                ) : (
                    <div className={styles.market_access_computed_date}>
                        {pendingExpirationDate
                            ? `${translations.users.marketAccess.endsOn} ${formattingService.toLongDayMonthYear(pendingExpirationDate)}`
                            : translations.users.marketAccess.noEndDate}
                    </div>
                )}
                {errorMessage && (
                    <div className={styles.market_access_error} role="alert">
                        {errorMessage}
                    </div>
                )}
                {warningMessage && (
                    <div className={styles.market_access_warning} role="alert">
                        {warningMessage}
                    </div>
                )}
            </div>
        </ActionModal>
    );
};

export default MarketAccessExpirationModal;
